import { useRef, type Dispatch, type SetStateAction } from "react";
import { Camera } from "lucide-react";
import ReceiptAttachments, { appendReceiptFiles, receiptGroups, type ReceiptAttachment } from "../../components/ReceiptAttachments";
import LoadingLabel from "../../components/LoadingLabel";

/** The first step of a receipt: choosing, pasting or photographing the images to read. */
export default function ReceiptCapture({ attachments, setAttachments, busy, onScan, setError, onAttachPhotos }: {
  attachments: ReceiptAttachment[];
  setAttachments: Dispatch<SetStateAction<ReceiptAttachment[]>>;
  busy: boolean;
  onScan: () => void;
  setError: (message: string) => void;
  onAttachPhotos?: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const receiptCount = receiptGroups(attachments).length;
  return (
    <>
      <label
        className={`receipt-upload${busy ? " is-busy" : ""}`}
        role="button"
        tabIndex={busy ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !busy) {
            e.preventDefault();
            fileInput.current?.click();
          }
        }}
      >
        <input
          ref={fileInput}
          className="sr-only"
          type="file"
          accept="image/*"
          multiple
          disabled={busy}
          aria-label="Receipt photo"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length) {
              if (files.some((image) => image.size > 20_000_000)) {
                setError("Choose an image under 20 MB.");
                e.target.value = "";
                return;
              }
              setAttachments((current) => appendReceiptFiles(current, files));
              setError("");
            }
            e.target.value = "";
          }}
        />
        <Camera size={30} />
        <strong>{busy ? <LoadingLabel>Reading receipts</LoadingLabel> : attachments.length ? "Add more photos" : "Add receipt photos"}</strong>
        <span>Select several receipts or extra photos of one receipt</span>
      </label>
      {!!attachments.length && <ReceiptAttachments items={attachments} onChange={setAttachments} disabled={busy} />}
      {!!attachments.length && onAttachPhotos && <button type="button" className="primary-button" disabled={busy} onClick={onAttachPhotos}>Attach photos without reading</button>}
      {!!attachments.length && <button type="button" className="primary-button" disabled={busy} onClick={onScan}>{busy ? <LoadingLabel>Reading receipts</LoadingLabel> : `Read ${receiptCount} ${receiptCount === 1 ? "receipt" : "receipts"}`}</button>}
      <button
        type="button"
        className="secondary-button"
        disabled={busy}
        onClick={async () => {
          try {
            if (!navigator.clipboard?.read)
              throw new Error(
                "Use Paste (⌘V / Ctrl+V) here, or choose a photo.",
              );
            const items = await navigator.clipboard.read();
            for (const item of items) {
              const type = item.types.find((t) => t.startsWith("image/"));
              if (type) {
                const blob = await item.getType(type);
                setAttachments((current) => appendReceiptFiles(current, [new File([blob], "pasted-receipt", { type })]));
                return;
              }
            }
            setError(
              "No image found. Copy a receipt image first, then paste it here.",
            );
          } catch {
            setError(
              "Could not access the clipboard. Use Paste (⌘V / Ctrl+V) here, or choose a photo.",
            );
          }
        }}
      >
        Paste receipt
      </button>
      <p className="smart-caption">
        {onAttachPhotos ? "Reading sends the photo to an AI provider (OpenAI or Gemini). Attaching photos alone does not. " : "The photo is sent to an AI provider (OpenAI or Gemini) to read. "}A saved receipt keeps its photo privately in
        Barnyard, along with the details you reviewed.
      </p>
    </>
  );
}
